import { useEffect, useRef } from 'react';
import './View.styles.css';
import { useParams } from 'react-router-dom';
import { FileUploadView } from '../../../../components/file-upload-view';
import { LinkView } from '../../../../components/link-view';
import { useReactToPrint } from 'react-to-print';
import { useService } from '../../Draft.hooks.ts';

import { ClockCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { FiPrinter } from 'react-icons/fi';
import { Breadcrumb } from 'antd';
import i18n from '../../../../utils/i18n.ts';
import type { FileUploadType } from '../../../../components/file-upload-view/FileUploadView.types.ts';
import { BREADCRUNBS } from '../../Draft.constants.tsx';
import dayjs from 'dayjs';
import { DATE_FORMAT } from '../../../../constants/app.ts';

const STATUS_MAP: Record<number, string> = {
  1: i18n.t('common.progress'),
  2: i18n.t('common.proposed'),
  3: i18n.t('common.published'),
};

export default function Content() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { regulation } = useService({ regulationId: Number(id) });

  const contentRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({ contentRef });

  useEffect(() => {
    document.title = `Entwurf ${regulation?.title ? ' | ' + regulation?.title : ''}`;
  }, [regulation]);

  return (
    <>
      <div className="breadcrumbs-container">
        <Breadcrumb className="breadcrumb" items={BREADCRUNBS} />
        <div onClick={reactToPrintFn} className="print-button">
          {t('common.print')} <FiPrinter />
        </div>
      </div>
      <div className="content-container">
        <div className="draft-page" ref={contentRef}>
          <div className="draft-title">{regulation?.title}</div>
          <div className="extra-info-row">
            <span className="extra-info-title draft-label">
              {t('pages.raw-regulations.creation')}
            </span>
            <span>
              {regulation?.creationDate ? dayjs(regulation?.creationDate).format(DATE_FORMAT) : '-'}
            </span>
          </div>
          <div className="extra-info-row">
            <span className="extra-info-title draft-label">{t('form.field.unit-in-charge')}</span>
            <span>{regulation?.unitInCharge?.title || '-'}</span>
          </div>
          <div className="extra-info-row">
            <span className="extra-info-title draft-label">{t('form.field.author')}</span>
            <span>{regulation?.author?.displayName || '-'}</span>
          </div>
          <div className="extra-info-row">
            <span className="extra-info-title draft-label">{t('form.field.topic')}</span>
            <span>
              {regulation?.topics?.length > 0
                ? regulation?.topics?.map((topic: { name: string }) => topic.name).join(', ')
                : '-'}
            </span>
          </div>
          <div className="extra-info-row">
            <span className="extra-info-title draft-label">{t('form.field.involved-units')}</span>
            <span>
              {regulation?.involvedUnits?.length > 0
                ? regulation?.involvedUnits.map((unit: { title: string }) => unit.title).join(', ')
                : '-'}
            </span>
          </div>
          <div className="extra-info-row">
            <span className="extra-info-title draft-label">
              {t('pages.raw-regulations.attachments')}
            </span>
            <span>{regulation?.attachments?.length || '-'}</span>
          </div>
          <div className="draft-status">
            <ClockCircleOutlined style={{ color: '#98cb00' }} />{' '}
            {regulation?.status ? STATUS_MAP[regulation?.status] : ''}
          </div>
          <div className="draft-en-summary">
            <span className="draft-label">{t('form.field.summary')}</span>
            <span dangerouslySetInnerHTML={{ __html: regulation?.shortDescription || '' }} />
          </div>
          <div className="draft-en-summary">
            <span className="draft-label">{t('form.field.summary-english')}</span>
            <span dangerouslySetInnerHTML={{ __html: regulation?.shortDescriptionEnglish || '' }} />
          </div>
          <div className="draft-en-summary">
            <span className="draft-label">{t('form.field.content')}</span>
            <span dangerouslySetInnerHTML={{ __html: regulation?.content || '' }} />
          </div>
          <div className="draft-links">
            <span className="draft-label">{t('form.field.links')}</span>
            {regulation?.links?.map((link: { title: string; url: string }) => (
              <a key={link.url} href={link.url} target="_blank">
                <LinkView link={link} />
              </a>
            ))}
          </div>
          <div className="draft-files">
            <span className="draft-label">{t('form.field.attachments')}</span>
            {regulation?.attachments?.map((attachment: FileUploadType) => (
              <a key={attachment.sharePointItemId} href={attachment.url} target="_blank">
                <FileUploadView file={attachment} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
